<template>
    <div>
        <RedesignBaseChart
            ref="baseChartRef"
            :widget="data"
            :option="chartOptions"
            classes="h-full min-h-52"
        >
            <template #post>
                <div class="custom-legend px-3">
                    <div
                        v-for="item in labelsListData"
                        :key="item.key"
                    >
                        <extension-dashboard-list-item
                            :color="item.itemStyle.color"
                            :label="item.name"
                            :value="item.duration"
                        />
                    </div>
                </div>
            </template>
        </RedesignBaseChart>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { WidgetExtended } from '@/types/dashboard'
import type { ECBasicOption } from 'echarts/types/dist/shared'
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import ExtensionDashboardListItem
    from '@/components/RedesignComponents/Cards/ExtensionsDashbordCards/ExtensionDashboardListItem.vue'
import { getEntityAccountStatusById } from '@/composables/globalData/useEntitiesData'
import redesignStatusTypes, { Statuses } from '@/enum/redesignStatusTypes'
import { ComponentExposed } from 'vue-component-type-helpers'
import { MergedExtension } from '@/types/extension'
import i18n from '@/i18n'

interface IChartData {
    name: string,
    value: number,
    duration: string,
    key: string
    itemStyle: { color: string }
    label: {color: string}
}

const props = defineProps<{
    data: WidgetExtended
    extension: MergedExtension
}>()

const baseChartRef = ref<ComponentExposed<typeof RedesignBaseChart>>()
const totalSumKey = 'TotalSum'
const breakTimeFields = [
    {
        key: 'Login Time',
        status: Statuses.LOGIN
    },
    {
        key: 'breaktimeAdministrative',
        status: Statuses.ADMINISTRATIVE
    },
    {
        key: 'breaktimeLunch',
        status: Statuses.LUNCH
    },
    {
        key: 'breakTimePrivate',
        status: Statuses.PRIVATE
    },
    {
        key: 'breakTimeOther',
        status: Statuses.OTHER
    },
    {
        key: 'breakTimeTraining',
        status: Statuses.TRAINING
    },
    {
        key: 'BreakTimeTeamMeeting',
        status: Statuses.TEAM_MEETING
    },
    {
        key: 'BreakTimeBrief',
        status: Statuses.BRIEF
    },
    {
        key: totalSumKey,
        status: -1
    }

]

const getStatusData = (accountId:number, statusId: number, key: string,) => {
    if(key === totalSumKey && statusId === -1) {
        return {
            Name: i18n.t('statistics.total.calls.duration') as string,
            ColorCode: redesignStatusTypes.value[Statuses.IN_CALL].color
        }
    }
    return getEntityAccountStatusById(accountId, statusId)

}

const chartData = computed(() => {
    return breakTimeFields.reduce<IChartData[]>((acc, { key, status }) => {
        const raw = props.extension.allDataSummary?.[key]
        if (typeof raw === 'string') {
            const seconds = durationToSeconds(raw)
            if (seconds > 0) {
                const statusData = getStatusData(props.extension.accountID, status, key)
                acc.push({
                    name: statusData?.Name ?? 'StatusName',
                    value: seconds,
                    key,
                    duration: raw,
                    itemStyle: { color: statusData?.ColorCode || '' },
                    label: {
                        color: statusData?.ColorCode || ''
                    }
                })
            }
        }
        return acc
    }, [])
})

const labelsListData = computed(() => {
    return chartData.value.filter(data => data.key !== totalSumKey)
})

const chartOptions = computed<ECBasicOption>(() => ({
    tooltip: {
        trigger: 'item',
        formatter: '{b}: {d}%',
        position: 'top'
    },
    legend: {
        show: false
    },
    series: [
        {
            type: 'pie',
            radius: [ '40%', '55%' ],
            center: [ '50%', '50%' ],
            avoidLabelOverlap: true,
            minAngle: 5,
            padAngle: 2,
            label: {
                show: true,
                position: 'outside',
                formatter: ({ percent }: { percent: number }) => `${percent.toFixed(1)}%`,
                fontSize: 12,
                distanceToLabelLine: 2,
                alignTo: 'labelLine',
                overflow: 'none',
                edgeDistance: '2%',
            },
            labelLine: {
                show: true,
                length: 8,
                length2: 4,
                smooth: true,
                lineStyle: {
                    width: 1
                },
                maxSurfaceAngle: 90
            },
            emphasis: {
                itemStyle: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                    shadowColor: 'rgba(0, 0, 0, 0.3)'
                }
            },
            data: chartData.value
        }
    ]

}))

const durationToSeconds = (time: string): number => {
    const [ h, m, s ] = time.split(':').map(Number)
    return h * 3600 + m * 60 + s
}

</script>

<style lang="scss" scoped>
</style>
